/**
 * Novidades (spec 036) — o contrato entre o SPA e a API.
 *
 * O CONTEÚDO das novidades mora no SPA (apps/web/src/features/novidades/
 * entries.ts, D1): sobe no mesmo PR da mudança que anuncia. A API não conhece
 * o texto; guarda só o que é da PESSOA (D2): quais novidades ela já viu e
 * quais tours já fez, por tenant, para o contador do megafone seguir a pessoa
 * entre máquinas.
 *
 * Por que um CONJUNTO de ids e não um "visto até a data X": a data da novidade
 * é a do PR, e o deploy vem depois (o SPA sobe no Pages, a API na esteira). Uma
 * pessoa que abriu o painel no dia 7 teria "visto" uma novidade datada do dia 6
 * que só entrou no ar no dia 8 — e ela nunca acenderia o contador. Com o id,
 * o que não foi aberto continua não visto, qualquer que seja a data.
 */
import { z } from 'zod';

/** id de novidade ou de tour: minúsculas, dígitos e hífen (`cotacao-por-km`) */
export const NEWS_ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const NEWS_ID_MAX_LENGTH = 80;
export const NewsIdSchema = z.string().max(NEWS_ID_MAX_LENGTH).regex(NEWS_ID_PATTERN);

/** quantos ids um "marcar como visto" leva de uma vez (o painel manda os que mostrou) */
export const NEWS_SEEN_BATCH_MAX = 50;
/**
 * Teto do que fica guardado por pessoa. O catálogo cresce umas dezenas por ano;
 * o teto existe para que um cliente que mande ids inventados encha só a PRÓPRIA
 * linha, e receba 422 em vez de crescer sem fim (CHECK na migration).
 */
export const NEWS_STATE_MAX_IDS = 1000;

export const NewsSeenBodySchema = z
  .object({ ids: z.array(NewsIdSchema).min(1).max(NEWS_SEEN_BATCH_MAX) })
  .strict();
export type NewsSeenBody = z.infer<typeof NewsSeenBodySchema>;

export const NewsTourParamSchema = z.object({ tourId: NewsIdSchema });

/**
 * O que o TENANT tem, para a novidade que depende disso (D3): a integração
 * com o sistema de origem do recurso (`sourceIntegration`, hoje o SGR — o nome
 * do sistema não vai em texto do bundle, como a chave das consultas dele em
 * sgr-queries.ts), o espelho do TiFlux e o mapa do Google na cotação.
 * Calculado no servidor; o SPA só filtra.
 */
export const NEWS_CAPABILITIES = ['sourceIntegration', 'tiflux', 'maps'] as const;
export type NewsCapability = (typeof NEWS_CAPABILITIES)[number];
export const NewsCapabilitiesSchema = z.object({
  sourceIntegration: z.boolean(),
  tiflux: z.boolean(),
  maps: z.boolean(),
});
export type NewsCapabilities = z.infer<typeof NewsCapabilitiesSchema>;
export const NO_NEWS_CAPABILITIES: NewsCapabilities = { sourceIntegration: false, tiflux: false, maps: false };

export const NewsMarksSchema = z.object({
  seenIds: z.array(z.string()),
  toursDone: z.array(z.string()),
});
export type NewsMarks = z.infer<typeof NewsMarksSchema>;

/** GET /news/state */
export const NewsStateSchema = NewsMarksSchema.extend({ capabilities: NewsCapabilitiesSchema });
export type NewsState = z.infer<typeof NewsStateSchema>;
